/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import { END } from 'redux-saga';
import {
    put, fork, call, select, take,
} from 'redux-saga/effects';
import mbpLogger from 'mbp-logger';

import {
    paypalGetClientId,
    logPaypalPaymentPayload,
    loadPaypalClientId,
    loadPaypalPayload,
    clearPaypalState,
    updatePaypalBillingAddressMissingMandoryFields,
} from './Paypal-Actions';
import {
    removeMaskForPhoneNumber,
    checkBillingAddressReturnedFromPaypal,
} from './Paypal-Helpers';

import { getPaypalPaymentDetails } from './Paypal-Selectors';

import cartServiceClient from '../../../../../../../apis/cart-service-apis/cartServiceClient';
import { checkJWT } from '../../../../../Member/ducks/Auth/Auth-Operations';

import takeLeading from '../../../../../../../app/helpers/sagas/takeLeading';
import { billingAddressIsFormAutoFilled, billingAddressIsFormValid, updateBillingAddressForm } from '../../BillingAddress/BillingAddress-Actions';
import { getOrderId } from '../../../../../Checkout/ducks/Global/Global-Selectors';
import { loadError } from '../../../../../Common/ducks/Error/Error-Actions';
import { paymentPlaceOrder } from '../../PlaceOrder/PlaceOrder-Actions';
import { getBillingAddress } from '../../../Cart/Cart-Selectors';

function* paypalGetClientIdWorker() {
    try {
        // clear error
        yield put(loadError({
            errType: 'paypal',
            errMsg: '',
        }));

        const jwtToken = yield call(checkJWT);
        const payload = {
            env: {},
            jwtToken,
            paymentMethod: 'PayPal',
        };
        // Get Paypal Session Client Token
        const response = yield call(cartServiceClient.paymentClientToken, payload);
        const { clientToken } = response.data;

        yield put(clearPaypalState());
        yield put(loadPaypalClientId(clientToken));
    } catch (ex) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            module: 'mbp-pwa-ui',
            function: 'paypalGetClientIdWorker',
            jsError: ex,
            message: 'Failed in getting paypal client id',
        });
        yield put(loadError({
            errType: 'paypal',
            errMsg: 'We are sorry, there seems to be a technical issue with paypal payment method, please try again later or use alternate payment method to complete order',
        }));
    }
}

function* paypalGetClientIdWatcher() {
    yield takeLeading(paypalGetClientId().type, paypalGetClientIdWorker);
}

function* workerPayPalPaymentMethodSelected(action) {
    const { payload } = action;
    const paypalDetails = yield select(getPaypalPaymentDetails);

    if (payload?.id === 'PayPal' && !paypalDetails?.payload?.nonce) {
        yield put(paypalGetClientId());
    }
}

function* watchForSetPaymentTypeAction() {
    const PAYMENT_SET_PAYMENT_METHOD = 'mbp-checkout/SET/PAYMENT/METHOD';
    let action = yield take(PAYMENT_SET_PAYMENT_METHOD);
    while (action !== END) {
        yield call(workerPayPalPaymentMethodSelected, action);
        action = yield take(PAYMENT_SET_PAYMENT_METHOD);
    }
}

function prepareBillingAddressFormData(payerInfo, billingForm) {
    const billingAddress = payerInfo?.billingAddress || {};
    const phoneNo = payerInfo?.phone ? removeMaskForPhoneNumber(payerInfo.phone) : '';
    const mobilePhoneNo = billingForm?.mobilePhone ? removeMaskForPhoneNumber(billingForm.mobilePhone) : phoneNo;
    return {
        address1: billingAddress.line1 || '',
        address2: billingAddress.line2 || '',
        zipCode: billingAddress.postalCode || '',
        state: billingAddress.state || '',
        country: billingAddress.countryCode || '',
        city: billingAddress.city || '',
        firstName: payerInfo.firstName || '',
        lastName: payerInfo.lastName || '',
        phone: phoneNo,
        mobilePhone: mobilePhoneNo,
        email: payerInfo.email || '',
        confirmEmail: payerInfo.email || '',
    };
}

function* submitPaypal(action) {
    try {
        const { history, payload } = action.data;
        const { nonce } = payload;
        const placeOrderConfig = {
            history,
            params: {},
            disablePaymentFields: false,
            source: 'PWA',
            paypalPayment: {
                nonce,
            },
        };
        yield put(paymentPlaceOrder(placeOrderConfig));
    } catch (ex) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            module: 'mbp-pwa-ui',
            function: 'submitPaypal',
            jsError: ex,
            message: 'Failed submitting paypal payment',
        });
    }
}

function* logPaypalPaymentPayloadWorker(action) {
    try {
        const { payload, history } = action.data;

        if (payload && payload.nonce) {
            const billingForm = yield select(getBillingAddress);
            const isBillingAddressAvailable = yield call(checkBillingAddressReturnedFromPaypal, payload.details);
            const billingFormData = yield call(prepareBillingAddressFormData, payload.details, billingForm);

            yield put(loadPaypalPayload(payload));
            yield put(updateBillingAddressForm(billingFormData));
            yield put(billingAddressIsFormAutoFilled(true));

            if (isBillingAddressAvailable) {
                yield put(billingAddressIsFormValid(true));
                yield put(updatePaypalBillingAddressMissingMandoryFields(false));
                yield call(submitPaypal, action);
            } else {
                yield put(billingAddressIsFormValid(false));
                yield put(updatePaypalBillingAddressMissingMandoryFields(true));

                if (history.location.pathname.indexOf('/checkout/review-order') >= 0) {
                    const orderId = yield select(getOrderId);
                    history.push(`/checkout/payment/${orderId}`);
                }
            }
        }
    } catch (ex) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            module: 'mbp-pwa-ui',
            function: 'logPaypalPaymentPayloadWorker',
            jsError: ex,
            message: 'Failed in loading paypal payment payload',
        });
    }
}

function* logPaypalPaymentPayloadWatcher() {
    let action = yield take(logPaypalPaymentPayload().type);
    while (action !== END) {
        yield call(logPaypalPaymentPayloadWorker, action);
        action = yield take(logPaypalPaymentPayload().type);
    }
}

const watchers = [
    fork(paypalGetClientIdWatcher),
    fork(logPaypalPaymentPayloadWatcher),
    fork(watchForSetPaymentTypeAction),
];

export {
    watchers,
};

export default {};
